import React, { useState, lazy, Suspense } from "react";
import TelaRelatorio, { TelaRelatorioKeys } from "../components/TelaRelatorio/TelaRelatorio";
import { useEntidades } from "../hooks/DropdownHooks";
import PAGINATION from "../utils/PaginacaoUtils";
import { useConfigCache } from "../utils/useConfigCache";
import api from "../services/api";
import { ColunasRelatorio, TipoRelatorio } from "../utils/PdfUtils";
import PdfAuditoria from "../utils/PdfAuditoria";
import DataUtils from "../utils/DataUtils";
import BotaoPadrao from "../components/BotaoPadrao/BotaoPadrao";
import DropdownPadrao from "../components/DropdownPadrao/DropdownPadrao";
import { IoAddOutline } from "react-icons/io5";
import ModalBuscaRelatorio, { TIPO_MODAL_RELATORIO } from "../components/ModalBuscaRelatorio/ModalBuscaRelatorio";
import ModalSistemas from "../components/ModalSistemas/ModalSistemas";
import NotificacaoUtils from "../utils/NotificacaoUtils";

const Carregamento = lazy(() => import("../components/Carregamento/Carregamento"));

const RelatorioAuditoria = () => {
    const [loading, setLoading] = useState(false);
    const [dataInicioState, setDataInicioState] = useState(null);
    const [dataFinalState, setDataFinalState] = useState(null);
    const [modalAberto, setModalAberto] = useState(false);
    const [usuariosSelecionados, setUsuariosSelecionados] = useState([]);
    const [totalSelecionado, setTotalSelecionado] = useState(0);
    const [itensNaoVinculadosModal, setItensNaoVinculadosModal] = useState([]);
    const [loadingModal, setLoadingModal] = useState(false);
    const [totalItensModal, setTotalItensModal] = useState(0);
    const [paginaAtualModal, setPaginaAtualModal] = useState(0);
    const tamanhoPaginaModal = PAGINATION.DEFAULT_PAGE_SIZE;
    const { options: entidadeOptions } = useEntidades();
    const [entidadeSelecionada, setEntidadeSelecionada] = useState([]);

    const configOptions = [
        { label: 'Incluir coluna de Endereço de IP', value: 'incluir_ip' },
    ];

    const {
        salvarConfig,
        setSalvarConfig,
        configSelecionadas,
        setConfigSelecionadas
    } = useConfigCache(TelaRelatorioKeys.AUDITORIA);

    const validateDates = () => {
        const { valido, mensagem } = DataUtils.validarPeriodoRelatorio(dataInicioState, dataFinalState);
        if (!valido) {
            NotificacaoUtils.erro(mensagem);
            return false;
        }
        return true;
    };

    const handleAbrirModalUsuarios = async () => {
        // Limpa o estado para garantir que não existam dados antigos
        setItensNaoVinculadosModal([]);
        setPaginaAtualModal(0);
        setTotalItensModal(0);
        
        // Primeiro abrimos o modal para mostrar o carregamento
        setModalAberto(true);
        setLoadingModal(true);
        
        try {
            const response = await api.get('/usuario_', {
                params: {
                    page: 0,
                    size: tamanhoPaginaModal
                }
            });
            const apiResponse = response.data;
            
            if (response.success) {
                // Garante que todos os estados sejam atualizados após receber a resposta
                setItensNaoVinculadosModal(apiResponse.content || []);
                const totalElements = apiResponse.page?.totalElements || 0;
                setTotalItensModal(totalElements);
                
            } else {
                console.error(response.message);
                setItensNaoVinculadosModal([]);
                setTotalItensModal(0);
            }
        } catch (error) {
            setItensNaoVinculadosModal([]);
            setTotalItensModal(0);
        } finally {
            setLoadingModal(false);
        }
    };
    
    // Função para carregar a próxima página de usuários no modal
    const carregarProximaPaginaModal = async () => {
        if (paginaAtualModal + 1 >= Math.ceil(totalItensModal / tamanhoPaginaModal)) return;
        
        setLoadingModal(true);
        try {
            const response = await api.get('/usuario_', {
                params: {
                    page: paginaAtualModal + 1,
                    size: tamanhoPaginaModal
                }
            });
            
            const apiResponse = response.data;
            if (response.success) {
                setItensNaoVinculadosModal(apiResponse.content || []);
                setPaginaAtualModal(paginaAtualModal + 1);
                // Atualiza total de itens se estiver disponível na resposta
                setTotalItensModal(apiResponse.page.totalElements);
            } 
        } catch (error) {
            console.error("Erro ao carregar próxima página:", error);
        } finally {
            setLoadingModal(false);
        }
    };
    
    // Função para carregar a página anterior de usuários no modal
    const carregarPaginaAnteriorModal = async () => {
        if (paginaAtualModal <= 0) return;
        
        setLoadingModal(true);
        try {
            const response = await api.get('/usuario_', {
                params: {
                    page: paginaAtualModal - 1,
                    size: tamanhoPaginaModal
                }
            });
            
            const apiResponse = response.data;
            if (response.success) {
                setItensNaoVinculadosModal(apiResponse.content || []);
                setPaginaAtualModal(paginaAtualModal - 1);
                setTotalItensModal(apiResponse.page.totalElements);
            } else {
                console.error(apiResponse.message);
            }
        } catch (error) {
            console.error("Erro ao carregar página anterior:", error);
        } finally {
            setLoadingModal(false);
        }
    };
    
    // Função para ir para uma página específica no modal
    const irParaPaginaModal = async (pagina) => {
        if (pagina < 0 || pagina >= Math.ceil(totalItensModal / tamanhoPaginaModal)) return;
        
        setLoadingModal(true);
        try {
            const response = await api.get('/usuario_', {
                params: {
                    page: pagina,
                    size: tamanhoPaginaModal
                }
            });
            
            const apiResponse = response.data;
            if (response.success) {
                setItensNaoVinculadosModal(apiResponse.content || []);
                setPaginaAtualModal(pagina);
                setTotalItensModal(apiResponse.page.totalElements);
            } else {
                console.error(apiResponse.message);
            }
        } catch (error) {
            console.error("Erro ao carregar página específica:", error);
        } finally {
            setLoadingModal(false);
        }
    };
    
    // Função para fechar o modal
    const handleFecharModal = () => {
        setModalAberto(false);
        setItensNaoVinculadosModal([]);
        setPaginaAtualModal(0);
        setLoadingModal(false); // Garante que o loading seja desativado ao fechar o modal
    };
    
    // Função para salvar os usuários selecionados
    const handleSalvarUsuarios = (dados) => {
        const { itensVinculados } = dados;
        setUsuariosSelecionados(itensVinculados);
        setTotalSelecionado(itensVinculados.length);
        setModalAberto(false);
        setLoadingModal(false); // Garante que o loading seja desativado ao salvar e fechar o modal
    };
    
    const handleGerarRelatorio = async () => {
        // Valida datas
        if (!validateDates()) return;
        setLoading(true);
        try {
            const titulo = "Relatório de Auditoria";
            const padraoRelatorioAuditoriaDTO = {
                tipoRelatorio: TipoRelatorio.AUDITORIA,
                dataInicial: DataUtils.formatDate(dataInicioState),
                dataFinal: DataUtils.formatDate(dataFinalState),
                entidadesSelecionadas: entidadeSelecionada,
                idsUsuariosAlteradores: usuariosSelecionados.length > 0 ? usuariosSelecionados.map(u => u.idUsuario || u.id) : []
            };

            const response = await api.post("/auditoria_/relatorio", padraoRelatorioAuditoriaDTO);
            
            const apiResponse = response;

            if (!response.success) {
                NotificacaoUtils.erro(response.message || "Erro ao gerar relatório.");
                return;
            }
            
            if(apiResponse.data.colunas.length === 0 || apiResponse.data.registros.length === 0) {
                NotificacaoUtils.aviso("Nenhum dado encontrado para gerar o relatório.");
                return;
            }

            const { colunas, registros } = apiResponse.data;

            // Se a configuração de IP estiver ativa, remover a coluna de IP
            let colunasFiltradas = colunas;
            let registrosFiltrados = registros;
            
            PdfAuditoria.gerarRelatorioAuditoria(
                titulo,
                colunasFiltradas.map(coluna => coluna.titulo),
                registrosFiltrados,
                { nomeArquivo: "relatorio_auditoria.pdf" }
            );

            NotificacaoUtils.sucesso("Relatório gerado com sucesso!");
        } catch (error) {
            console.error("Erro ao gerar relatório:", error);
            NotificacaoUtils.erro("Erro ao gerar relatório. Tente novamente mais tarde.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            {(loading || loadingModal) && <Carregamento />}

            <TelaRelatorio
                tituloPagina={"Relatório - Auditoria"}
                tituloContainer={"Filtros Opcionais"}
                subtituloContainer={"Escolha itens específicos para ser visualizados no relatório."}
                tituloContainerPeriodo={"Período do Relatório"}
                subtituloContainerPeriodo={"Selecione o período a ser exibido no relatório."}
                dataInicio={dataInicioState}
                horaFinal={dataFinalState}
                onChangeDataInicio={setDataInicioState}
                onChangeHoraFinal={setDataFinalState}
                gerarRelatorio={handleGerarRelatorio}
                storageKey={TelaRelatorioKeys.AUDITORIA}
                exibeContainerPeriodo={true}
            > 
                <DropdownPadrao
                    multiple
                    value={entidadeSelecionada}
                    onChange={e => setEntidadeSelecionada(e.target.value)}
                    options={entidadeOptions}
                    cresce100={true}
                    label="Entidade"
                >
                </DropdownPadrao>

                <BotaoPadrao
                    isConfirmar={false}
                    icon={<IoAddOutline />}
                    aoClicar={handleAbrirModalUsuarios}
                    texto={`Usuário Alterador (${totalSelecionado})`}
                >
                </BotaoPadrao>
            </TelaRelatorio>

            <ModalSistemas
                aberto={modalAberto}
                titulo="Selecionar Usuários"
                onClose={handleFecharModal}
                tamanhoEtapa={60} 
                className="modal-busca-usuarios"
                subtitulo={"Selecione os usuários que serão utilizados como filtro para o relatório de auditoria."}
            >
                <ModalBuscaRelatorio
                    tipoModal={TIPO_MODAL_RELATORIO.USUARIO}
                    onCancelar={handleFecharModal}
                    onSalvar={handleSalvarUsuarios}
                    itensNaoVinculados={itensNaoVinculadosModal}
                    itensVinculados={usuariosSelecionados}
                    loading={loadingModal}
                    totalRegistros={totalItensModal}
                    paginaAtual={paginaAtualModal}
                    tamanhoPagina={tamanhoPaginaModal}
                    proximaPagina={carregarProximaPaginaModal}
                    paginaAnterior={carregarPaginaAnteriorModal}
                    irParaPagina={irParaPaginaModal}
                    onPaginaChange={setPaginaAtualModal}
                />
            </ModalSistemas>
        </div>
    );
};

export default RelatorioAuditoria;
